import { defineStore } from 'pinia'

import { useInterfaceStore } from 'src/stores/interface.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useNotificationsStore } from 'src/stores/notifications.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  approveUser,
  denyUser,
  fetchFollowRequests,
  fetchOutgoingFollowRequests,
} from 'src/api/user.js'
import { pollingActions } from 'src/services/poller/poller.js'

// your own waiting requests change rarely: every sixth run, once a minute
const OUTGOING_EVERY = 6

export const useFollowRequestsStore = defineStore('followRequests', {
  state: () => ({
    poller: null,
    // polls since the start, for the rarer check of your own requests
    pollRuns: 0,
    requests: new Map(),
    // requests you sent, not answered yet
    outgoing: [],
    showingApproveConfirmDialog: false,
    showingDenyConfirmDialog: false,
    tempId: null,
  }),
  getters: {
    followRequestsCount(state) {
      return state.requests.size
    },
    // the page has something to show, either way
    hasFollowRequests(state) {
      return state.requests.size > 0 || state.outgoing.length > 0
    },
  },
  actions: {
    ...pollingActions((store) => store.loadFollowRequests(), 10000),
    // at logout: the next account starts with empty lists
    stopFetching() {
      this.poller?.stop()
      this.pollRuns = 0
      this.requests = new Map()
      this.outgoing = []
    },
    // requests to you, and every sixth time (once a minute) your own
    loadFollowRequests() {
      const outgoing = this.pollRuns++ % OUTGOING_EVERY === 0
      return Promise.all([
        this.fetchIncoming(),
        outgoing ? this.fetchOutgoing() : null,
      ])
    },
    // only a locked account gets requests to answer
    async fetchIncoming() {
      if (!useUsersStore().currentUser?.locked) {
        if (this.requests.size) this.setFollowRequests([])
        return
      }
      const result = await fetchFollowRequests({})
      this.setFollowRequests(result.data)
      useUsersStore().addNewUsers(result)
    },
    setFollowRequests(requests) {
      this.requests = new Map(requests.map((user) => [user.id, user]))
    },
    async fetchOutgoing() {
      try {
        const result = await fetchOutgoingFollowRequests({})
        useUsersStore().addNewUsers(result)
        this.outgoing = result.data
      } catch {
        // an optional endpoint: keep the list as it is
      }
    },
    async cancelOutgoing(user) {
      // gone from the list at once, so a second click cannot send it again
      this.outgoing = this.outgoing.filter((u) => u.id !== user.id)
      try {
        await useUsersStore().unfollowUser(user.id)
      } catch {
        this.outgoing = [...this.outgoing, user]
        useInterfaceStore().pushGlobalNotice({
          level: 'error',
          messageKey: 'follow_requests.cancel_error',
        })
      }
    },

    // Confirm dialogs
    showApproveConfirmDialog(id) {
      this.showingApproveConfirmDialog = true
      this.tempId = id
    },
    showDenyConfirmDialog(id) {
      this.showingDenyConfirmDialog = true
      this.tempId = id
    },
    hideApproveConfirmDialog() {
      this.showingApproveConfirmDialog = false
      this.tempId = null
    },
    hideDenyConfirmDialog() {
      this.showingDenyConfirmDialog = false
      this.tempId = null
    },

    // Dialog/Instant fork
    approve(id) {
      if (useMergedConfigStore().mergedConfig.modalOnApproveFollow) {
        this.showApproveConfirmDialog(id)
      } else {
        this.doApprove(id)
      }
    },
    deny(id) {
      if (useMergedConfigStore().mergedConfig.modalOnDenyFollow) {
        this.showDenyConfirmDialog(id)
      } else {
        this.doDeny(id)
      }
    },

    // Actual calls
    async doApprove(userId) {
      const id = userId ?? this.tempId
      this.hideApproveConfirmDialog()

      try {
        await approveUser({
          id,
        })

        const notifId = this.findFollowRequestNotificationId(id)
        notifId && useNotificationsStore().markSingleNotificationAsSeen(notifId)
        this.requests.delete(id)
      } catch (error) {
        useInterfaceStore().pushGlobalNotice({
          messageKey: 'user_card.approve_error',
          messageArgs: { error },
          level: 'error',
        })
      }
    },
    async doDeny(userId) {
      const id = userId ?? this.tempId
      this.hideDenyConfirmDialog()

      try {
        await denyUser({
          id,
        })

        const notifId = this.findFollowRequestNotificationId(id)
        notifId && useNotificationsStore().markSingleNotificationAsSeen(notifId)
        this.requests.delete(id)
      } catch (error) {
        useInterfaceStore().pushGlobalNotice({
          messageKey: 'user_card.deny_error',
          messageArgs: { error },
          level: 'error',
        })
      }
    },

    // Utility
    findFollowRequestNotificationId(userId) {
      const notif = useNotificationsStore().data.find(
        (notif) =>
          notif.from_profile.id === userId && notif.type === 'follow_request',
      )
      return notif?.id
    },
  },
})
